import React, { useState } from "react";
import {
    Button, Divider, Grid, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, OutlinedInput, Select, Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLicitacoesState } from "../context/licitacoes-context";
import { useLicitacoesController } from "../context/licitacoes-controller";
import { ILicitacaoParticipanteRequest } from "../../../../model/admin-portal/request/licitacao-participante-request";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import { CurrencyInput } from "../../../components/currency-input";

const emptyDraft: ILicitacaoParticipanteRequest = { id: 0, idFornecedor: null, cnpjcpf: '', resultado: '', valorProposto: 0 };

export const LicitacaoParticipantesSection = () => {
    const { dataRequest, fornecedoresList } = useLicitacoesState();
    const { saveParticipante, removeParticipante } = useLicitacoesController();
    const [draft, setDraft] = useState<ILicitacaoParticipanteRequest>(emptyDraft);
    const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);

    const getFornecedorName = (id: number) => fornecedoresList?.find((f) => f.id === id)?.name || '';

    const handleEdit = (index: number) => {
        setDraft(dataRequest.participantes[index]);
        setEditingIndex(index);
    };

    const handleCancel = () => {
        setDraft(emptyDraft);
        setEditingIndex(undefined);
    };

    const handleSave = () => {
        if (!draft.idFornecedor || !draft.cnpjcpf || !draft.resultado || draft.valorProposto <= 0) return;
        saveParticipante(draft, editingIndex);
        handleCancel();
    };

    return (
        <Grid item lg={12} sm={12} xl={12}>
            <Typography variant="subtitle1" fontWeight="bold">Participantes</Typography>
            <Divider sx={{ mb: 1 }} />

            {dataRequest.participantes.length > 0 && (
                <List dense>
                    {dataRequest.participantes.map((participante, index) => (
                        <ListItem
                            key={index}
                            divider
                            secondaryAction={
                                <React.Fragment>
                                    <IconButton size="small" onClick={() => handleEdit(index)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => removeParticipante(index)}><DeleteIcon fontSize="small" /></IconButton>
                                </React.Fragment>
                            }
                        >
                            <ListItemText
                                primary={`${getFornecedorName(participante.idFornecedor as number)} (${participante.cnpjcpf})`}
                                secondary={`${participante.resultado} - ${participante.valorProposto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item lg={4} sm={6} xl={4}>
                    <InputLabel>Fornecedor *</InputLabel>
                    <Select
                        size="small"
                        error={false}
                        value={draft.idFornecedor ?? ''}
                        onChange={(e) => setDraft({ ...draft, idFornecedor: e.target.value as number })}
                        fullWidth
                    >
                        <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                        {fornecedoresList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((f) => <MenuItem value={f.id} key={f.id}>{f.name}</MenuItem>)}
                    </Select>
                </Grid>
                <Grid item lg={2} sm={3} xl={2}>
                    <InputLabel>CNPJ/CPF *</InputLabel>
                    <TextInputWithCounter
                        size="small"
                        maxLength={18}
                        error={false}
                        value={draft.cnpjcpf}
                        onChange={(e) => setDraft({ ...draft, cnpjcpf: e.target.value })}
                        fullWidth
                    />
                </Grid>
                <Grid item lg={2} sm={3} xl={2}>
                    <InputLabel>Resultado *</InputLabel>
                    <TextInputWithCounter
                        size="small"
                        maxLength={30}
                        error={false}
                        value={draft.resultado}
                        onChange={(e) => setDraft({ ...draft, resultado: e.target.value })}
                        fullWidth
                    />
                </Grid>
                <Grid item lg={2} sm={6} xl={2}>
                    <InputLabel>Valor Proposto *</InputLabel>
                    <CurrencyInput
                        size="small"
                        error={false}
                        value={draft.valorProposto}
                        onChange={(value) => setDraft({ ...draft, valorProposto: value })}
                        fullWidth
                    />
                </Grid>
                <Grid item lg={2} sm={12} xl={2} sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={editingIndex === undefined ? <AddIcon /> : undefined}
                        onClick={handleSave}
                        disabled={!draft.idFornecedor || !draft.cnpjcpf || !draft.resultado || draft.valorProposto <= 0}
                    >
                        {editingIndex === undefined ? 'Adicionar' : 'Salvar'}
                    </Button>
                    {editingIndex !== undefined && (
                        <Button size="small" onClick={handleCancel}>Cancelar</Button>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};
